import type { components } from '@trackercop/contracts';

type ScriptCategory = components['schemas']['ScriptCategory'];
type CookieCategory = components['schemas']['CookieCategory'];
type PayloadCategory = components['schemas']['PayloadCategory'];

// Stable colour assignment for category chips. Referenced from CLAUDE.md —
// every view that renders a category (scripts, cookies, payloads, GTM
// inventory) shares this palette so a reviewer learns one mental map.
// Values are Tailwind tokens; background uses `/15` alpha so chips stay
// legible against the neutral base theme. Do not add ad-hoc colours in
// components — add the category here first.
export const scriptCategoryPalette: Record<ScriptCategory, { bg: string; text: string; label: string }> = {
  advertising: { bg: 'bg-red-600/15', text: 'text-red-700', label: 'Advertising' },
  marketing: { bg: 'bg-rose-500/15', text: 'text-rose-700', label: 'Marketing' },
  analytics: { bg: 'bg-sky-600/15', text: 'text-sky-700', label: 'Analytics' },
  social: { bg: 'bg-violet-600/15', text: 'text-violet-700', label: 'Social' },
  'customer-data-platform': { bg: 'bg-fuchsia-600/15', text: 'text-fuchsia-700', label: 'CDP' },
  'session-replay': { bg: 'bg-orange-600/15', text: 'text-orange-700', label: 'Session replay' },
  'ab-testing': { bg: 'bg-amber-500/15', text: 'text-amber-700', label: 'A/B testing' },
  'consent-management': { bg: 'bg-emerald-600/15', text: 'text-emerald-700', label: 'Consent management' },
  'tag-manager': { bg: 'bg-indigo-600/15', text: 'text-indigo-700', label: 'Tag manager' },
  cdn: { bg: 'bg-slate-500/15', text: 'text-slate-700', label: 'CDN' },
  unknown: { bg: 'bg-neutral-400/15', text: 'text-neutral-700', label: 'Unknown' },
};

// Cookie palette reuses the script palette where categories overlap and
// adds `strictly-necessary` + `functional`. Keep in sync with the
// ScriptCategory palette above — shared values must resolve to the same
// colour so a reviewer learns one map.
export const cookieCategoryPalette: Record<CookieCategory, { bg: string; text: string; label: string }> = {
  'strictly-necessary': { bg: 'bg-teal-600/15', text: 'text-teal-700', label: 'Strictly necessary' },
  functional: { bg: 'bg-cyan-600/15', text: 'text-cyan-700', label: 'Functional' },
  analytics: { bg: 'bg-sky-600/15', text: 'text-sky-700', label: 'Analytics' },
  advertising: { bg: 'bg-red-600/15', text: 'text-red-700', label: 'Advertising' },
  marketing: { bg: 'bg-rose-500/15', text: 'text-rose-700', label: 'Marketing' },
  social: { bg: 'bg-violet-600/15', text: 'text-violet-700', label: 'Social' },
  cdn: { bg: 'bg-slate-500/15', text: 'text-slate-700', label: 'CDN' },
  unknown: { bg: 'bg-neutral-400/15', text: 'text-neutral-700', label: 'Unknown' },
};

// Payload palette distinguishes data-sensitivity categories. PII is red
// (highest sensitivity), descending through identifier/device-fingerprint
// (orange/amber), behavioral (yellow), advertising (purple), product
// (blue), benign (grey). The colours inform the data-sharing-map edge
// colours when that graph lands in Session 9 — keep them stable.
export const payloadCategoryPalette: Record<PayloadCategory, { bg: string; text: string; label: string }> = {
  pii: { bg: 'bg-red-600/20', text: 'text-red-700', label: 'PII' },
  identifier: { bg: 'bg-orange-500/20', text: 'text-orange-700', label: 'Identifier' },
  'device-fingerprint': { bg: 'bg-amber-500/20', text: 'text-amber-700', label: 'Device fingerprint' },
  behavioral: { bg: 'bg-yellow-500/20', text: 'text-yellow-800', label: 'Behavioral' },
  advertising: { bg: 'bg-purple-600/20', text: 'text-purple-700', label: 'Advertising' },
  product: { bg: 'bg-blue-600/20', text: 'text-blue-700', label: 'Product' },
  benign: { bg: 'bg-neutral-400/15', text: 'text-neutral-700', label: 'Benign' },
};
